import { Chip, Paper, TextField } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import React, { useEffect, useRef, useState } from "react";
import { isArray, isString } from "lodash";
import toastError from "../../errors/toastError";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
    container: {
        display: "flex",
        alignItems: "center",
        padding: "4px 12px",
        backgroundColor: theme.palette.type === "dark" ? "#1E293B" : "#FAFBFC",
        borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#E2E8F0"}`,
        boxShadow: "none",
        borderRadius: 0,
        minHeight: 36,
    },
    autocomplete: {
        flex: 1,
        "& .MuiAutocomplete-inputRoot": {
            fontFamily: '"Inter", "Poppins", sans-serif',
            fontSize: 13,
            padding: "2px 4px !important",
            flexWrap: "wrap",
            gap: 4,
        },
        "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
        },
        "& .MuiAutocomplete-input": {
            fontFamily: '"Inter", "Poppins", sans-serif',
            fontSize: 13,
            padding: "2px 4px !important",
            minWidth: 80,
            "&::placeholder": {
                color: theme.palette.type === "dark" ? "#64748B" : "#94A3B8",
                opacity: 1,
                fontStyle: "italic",
            },
        },
        "& .MuiAutocomplete-tag": {
            margin: "1px 2px",
        },
    },
    chip: {
        fontFamily: '"Inter", "Poppins", sans-serif',
        fontSize: "0.7rem",
        fontWeight: 600,
        height: 22,
        borderRadius: 6,
        color: "#FFFFFF",
        border: "none",
        padding: "0 2px",
        "& .MuiChip-deleteIcon": {
            color: "rgba(255,255,255,0.7)",
            fontSize: 16,
            "&:hover": {
                color: "#FFFFFF",
            },
        },
    },
    dropdownPaper: {
        borderRadius: 10,
        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
        border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "#E2E8F0"}`,
        marginTop: 4,
        fontFamily: '"Inter", "Poppins", sans-serif',
        "& .MuiAutocomplete-option": {
            fontFamily: '"Inter", "Poppins", sans-serif',
            fontSize: 13,
            padding: "6px 12px",
            borderRadius: 6,
            margin: "2px 4px",
            "&:hover": {
                backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#F1F5F9",
            },
        },
    },
}));

export function TagsContainer({ contact, ticket, kanban }) {
    const classes = useStyles();

    const [tags, setTags] = useState([]);
    const [selecteds, setSelecteds] = useState([]);
    const isMounted = useRef(true);
    
    useEffect(() => {
        return () => {
            isMounted.current = false
        }
    }, [])

    useEffect(() => {
        if (isMounted.current) {
            loadTags().then(() => {
                let tags = []
                if (ticket && Array.isArray(ticket?.tags)) {
                    tags = [...tags,...ticket.tags]                
                } 
                if (Array.isArray(contact?.tags)) {
                    tags = [...tags,...contact.tags]                
                } 
                setSelecteds(tags);
            });
        }
    }, [contact, ticket]);

    const createTag = async (data) => {
        try {
            const { data: responseData } = await api.post(`/tags`, data);
            return responseData;
        } catch (err) {
            toastError(err);
        }
    }

    const loadTags = async () => {
        try {            
            const { data } = await api.get(`/tags/list`, {
                params:{
                    kanban
                }
            });
                                    
            setTags(data);
        } catch (err) {
            toastError(err);
        }
    }

    const syncTags = async (data) => {
        try {
            const { data: responseData } = await api.post(`/tags/sync`, data);
            return responseData;
        } catch (err) {
            toastError(err);
        }
    }

    const onChange = async (value, reason) => {
        let optionsChanged = []
        if (reason === 'create-option') {
            if (isArray(value)) {
                for (let item of value) {
                    if (item.length < 3) {
                        toastError("Tag muito curta!");
                        return;
                    }
                    if (isString(item)) {
                        const newTag = await createTag({ name: item, kanban: 0, color: getRandomHexColor() })
                        optionsChanged.push(newTag);
                    } else {
                        optionsChanged.push(item);
                    }
                }
            }
            await loadTags();
        } else {
            optionsChanged = value;
        }
        setSelecteds(optionsChanged);
        await syncTags({ contactId: contact?.id,ticketId: ticket?.id , tags: optionsChanged });
    }

    function getRandomHexColor() {
        const red = Math.floor(Math.random() * 256);
        const green = Math.floor(Math.random() * 256);
        const blue = Math.floor(Math.random() * 256);
        const hexColor = `#${red.toString(16).padStart(2, '0')}${green.toString(16).padStart(2, '0')}${blue.toString(16).padStart(2, '0')}`;
        return hexColor;
    }

    return (
        <Paper elevation={0} className={classes.container}>
            <Autocomplete
                multiple
                size="small"
                options={tags}
                value={selecteds}
                freeSolo
                className={classes.autocomplete}
                onChange={(e, v, r) => onChange(v, r)}
                getOptionLabel={(option) => option.name}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        <Chip
                            className={classes.chip}
                            style={{
                                backgroundColor: option.color || '#94A3B8',
                            }}
                            label={option.name}
                            {...getTagProps({ index })}
                            size="small"
                        />
                    ))
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Adicionar tag..."
                    />
                )}
                PaperComponent={({ children }) => (
                    <Paper className={classes.dropdownPaper}>
                        {children}
                    </Paper>
                )}
            />
        </Paper>
    )
}
